import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { AppBar, Button, Container, makeStyles, Toolbar, Typography, useTheme } from '@material-ui/core'

import { AddOutlined } from '@material-ui/icons'
import Confirmation from '../../../components/Dialogs/Misc/Confirmation'
import Contact from '../../../components/Cards/Contacts/Contact'
import LoadingOverlay from '../../../components/Misc/LoadingOverlay'

import { showError } from '../../../actions/misc/misc.actions'

import { isEqual, cloneDeep } from 'lodash'

import { isEmailValid } from '../../../services/helpers'

import { postAccounts, deactivate, activate } from '../../../actions/accounts/accounts.actions'

import { v4 as uuid } from 'uuid'

import { api } from '../../../services/api'

const useStyles = makeStyles((theme) => ({
	root: {
		flexGrow: 1,
		paddingTop: '24px',
		paddingLeft: '20px',
		paddingRight: '20px',
		paddingBottom: '75px'
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		marginBottom: 24
	},
	appBar: {
		top: 'auto',
		bottom: 0,
		paddingRight: '0px !important',
		zIndex: 1201
	}
}))

const Contacts = () => {
	const classes = useStyles()
	const theme = useTheme()
	const dispatch = useDispatch()
	const household = useSelector((state) => state.specific.households.selected)
	const householdLoading = useSelector((state) => state.specific.households.loading)
	const current = useSelector((state) => state.specific.accounts.accounts)
	const accountsLoading = useSelector((state) => state.specific.accounts.loading)
	const error = useSelector((state) => state.specific.accounts.error)

	const [isSaveShown, setIsSaveShown] = useState(false)
	const [accounts, setAccounts] = useState([])
	const [originalAccounts, setOriginalAccounts] = useState([])
	const [accountIdsToDelete, setAccountIdsToDelete] = useState([])
	const [confirm, setConfirm] = useState(false)
	const [fetchingFirebaseStatuses, setFetchingFirebaseStatuses] = useState(false)

	useEffect(() => {
		if (household && !current) {
			dispatch({ type: 'SET_ACCOUNTS', payload: household.accounts })
		}

		if (current) {
			let updatedAccounts = current.map((account) => ({
				...account,
				makePrimary: false
			}))

			setOriginalAccounts(cloneDeep(updatedAccounts))
			setAccounts([...updatedAccounts])
		}
	}, [current, household])

	useEffect(() => {
		if (accounts && originalAccounts && !fetchingFirebaseStatuses) {
			let equal = !isEqual(accounts, originalAccounts)

			setIsSaveShown(equal || accountIdsToDelete.length !== 0)
		}
	}, [accounts, originalAccounts, accountIdsToDelete, fetchingFirebaseStatuses])

	useEffect(() => {
		const fetchFirebaseAccountsStatus = async () => {
			setFetchingFirebaseStatuses(true)
			try {
				const { data } = await api.get(`/clients/${household._id}/firebaseAccountsStatus`)
				if (data.data && data.data.length > 0) {
					const info = data.data[0]
					setAccounts((prev) => {
						const updated = prev.map((account) => {
							const { _id } = account
							if (info[_id]) {
								account['firebase_disabled'] = info[_id].disabled
							}
							return account
						})
						return updated
					})
					setOriginalAccounts((prev) => {
						const updated = prev.map((account) => {
							const { _id } = account
							if (info[_id]) {
								account['firebase_disabled'] = info[_id].disabled
							}
							return account
						})
						return updated
					})
				}
				if (data.errors && data.errors.length > 0) {
					console.log('error getting firebase status for account', data.errors[0])
				}
			} catch (e) {
				console.log('error getting firebase status for account', e)
			} finally {
				setFetchingFirebaseStatuses(false)
			}
		}

		if (household?._id > '') {
			fetchFirebaseAccountsStatus()
		}
	}, [household])

	const areAccountsValid = () => {
		return accounts.every((account) => account.first_name && account.last_name && account.email)
	}

	const saveHousehold = (specialToken) => {
		if (!areAccountsValid()) {
			dispatch(showError('One or more accounts are missing either first name, last name or email.'))
			return
		} else if (!accounts.every((account) => isEmailValid(account.email))) {
			dispatch(showError('Check the email(s) on the contacts you have created. One or more are invalid.'))
			return
		}

		dispatch(postAccounts(household._id, { accounts, accountIdsToDelete, specialToken })).then((res) => {
			console.log('big res here..', res)

			setAccountIdsToDelete([])
		})
	}

	const activateAccount = (account) => {
		dispatch(activate(account._id))
	}

	const deactivateAccount = (account) => {
		dispatch(deactivate(account._id))
	}

	useEffect(() => {
		if (!accountsLoading && error) {
			dispatch(showError(error))
		}
	}, [accountsLoading, error])

	const clearChanges = () => {
		let updatedAccounts = originalAccounts
		updatedAccounts = updatedAccounts.filter((account) => account._id)

		setOriginalAccounts(cloneDeep(updatedAccounts))
		setAccounts([...updatedAccounts])
	}

	const addAccount = () => {
		let newAccount = {
			first_name: '',
			last_name: '',
			phone_number: '',
			email: '',
			makePrimary: false,
			legalAgreement: false,
			new: true,
			guid: uuid()
		}

		let updatedAccounts = [...accounts, newAccount]

		setAccounts(cloneDeep(updatedAccounts))
	}

	const updateAccount = (contact, field, value) => {
		let updatedAccounts = [...accounts]
		let accountIndex = updatedAccounts.findIndex((account) => account === contact)

		if (accountIndex !== -1) {
			if (field === 'makePrimary') {
				updatedAccounts.forEach((a) => {
					a.makePrimary = false
				})
			}

			updatedAccounts[accountIndex][field] = value

			if (field === 'ID_info') {
				if (typeof updatedAccounts[accountIndex][field] === 'object') {
					if (
						!Object.keys(updatedAccounts[accountIndex][field]).some(
							(f) => updatedAccounts[accountIndex][field][f]
						)
					) {
						delete updatedAccounts[accountIndex][field]
					}
				}
			}
		}

		setAccounts(updatedAccounts)
	}

	const removeAccount = (contact) => {
		let updatedAccounts = [...accounts]
		let accountIndex = updatedAccounts.findIndex((account) => account === contact)

		if (accountIndex !== -1) {
			if (updatedAccounts[accountIndex]._id) {
				setAccountIdsToDelete([...accountIdsToDelete, updatedAccounts[accountIndex]._id])
			}

			updatedAccounts.splice(accountIndex, 1)
		}

		setOriginalAccounts(cloneDeep(updatedAccounts))
		setAccounts(updatedAccounts)
	}

	return (
		<Container className={classes.root} maxWidth="md">
			<div className={classes.header}>
				<Typography variant="h5" style={{ fontWeight: 800 }}>
					Contacts
				</Typography>
				<Button
					variant="outlined"
					style={{ marginLeft: 16, paddingLeft: 34, paddingRight: 40 }}
					startIcon={<AddOutlined style={{ fill: theme.palette.secondary.main }} />}
					onClick={addAccount}
					color="secondary">
					Contact
				</Button>
			</div>
			{accounts && accounts.length ? (
				<div>
					{accounts.map((contact) => (
						<Contact
							key={contact && contact._id ? contact._id : contact.guid}
							pets={household?.pets || []}
							contact={contact}
							firebaseDisabled={contact.firebase_disabled || false}
							updateAccount={updateAccount}
							removeAccount={removeAccount}
							activateAccount={activateAccount}
							deactivateAccount={deactivateAccount}
						/>
					))}
					{isSaveShown ? (
						<AppBar position="fixed" color="primary" className={classes.appBar}>
							<Toolbar>
								<Container maxWidth="md">
									<Button
										variant="contained"
										color="secondary"
										disabled={householdLoading || accountsLoading}
										style={{ marginRight: '8px' }}
										onClick={() => {
											if (accounts.find((a) => !a._id)) {
												setConfirm(true)
											} else {
												let token = 'Cool Token Here'
												saveHousehold(token)
											}
										}}>
										Save Changes
									</Button>
									<Button variant="outlined" color="secondary" onClick={clearChanges}>
										Cancel
									</Button>
								</Container>
							</Toolbar>
						</AppBar>
					) : null}
				</div>
			) : null}
			{householdLoading || accountsLoading ? <LoadingOverlay /> : null}
			<Confirmation
				confirm={() => {
					let token = 'Cool Token Here'
					saveHousehold(token)
				}}
				handleCancel={() => setConfirm(false)}
				isShown={confirm}
				setIsShown={(v) => setConfirm(v)}
				message="Do you want to create this new contact? They will be able to log in to the client account."
			/>
		</Container>
	)
}

export default Contacts
